import { useState, useEffect, useCallback } from 'react';
import {
  listUsers,
  updateUser,
  deleteUser,
  type UpdateUserInput,
} from '@/services/admin';
import type { User } from '@/types';

export const useAdminUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchUsers = useCallback(async (searchTerm?: string) => {
    try {
      setLoading(true);
      setError(null);
      const result = await listUsers({ search: searchTerm });
      setUsers(result.items);
      setTotal(result.total);
      setHasMore(result.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(search);
  }, [fetchUsers, search]);

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
  }, []);

  const update = async (uid: string, input: UpdateUserInput) => {
    try {
      const updatedUser = await updateUser(uid, input);
      setUsers((prev) =>
        prev.map((u) => (u.uid === uid ? updatedUser : u))
      );
      return updatedUser;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update user';
      setError(message);
      throw err;
    }
  };

  const remove = async (uid: string) => {
    try {
      await deleteUser(uid);
      setUsers((prev) => prev.filter((u) => u.uid !== uid));
      setTotal((prev) => prev - 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete user';
      setError(message);
      throw err;
    }
  };

  const makeAdmin = async (uid: string) => {
    return update(uid, { role: 'admin' });
  };

  const removeAdmin = async (uid: string) => {
    return update(uid, { role: 'user' });
  };

  const enableUser = async (uid: string) => {
    return update(uid, { disabled: false });
  };

  const disableUser = async (uid: string) => {
    return update(uid, { disabled: true });
  };

  const updateQuota = async (uid: string, quota: number) => {
    return update(uid, { dailyDeeplinkQuota: quota });
  };

  return {
    users,
    loading,
    error,
    search,
    total,
    hasMore,
    handleSearch,
    update,
    remove,
    makeAdmin,
    removeAdmin,
    enableUser,
    disableUser,
    updateQuota,
    refresh: () => fetchUsers(search),
  };
};
